/**
 * VendingDashboardPage - Vending platform dashboard with transaction/revenue metrics
 */

import { Page, View, TableView } from 'web-mojo';
import { MetricsMiniChartWidget } from 'web-mojo/charts';
import { VendingTransactionList } from '@portal/models/VendingTransaction.js';


class MetricsView extends View {
    constructor(options = {}) {
        super({ ...options, className: 'metrics-section' });
    }

    async getTemplate() {
        return `
        <div class="row g-3 mb-4">
            <div class="col-xl-2 col-lg-4 col-6">
                <div data-container="metric_transactions"></div>
            </div>
            <div class="col-xl-2 col-lg-4 col-6">
                <div data-container="metric_revenue"></div>
            </div>
            <div class="col-xl-2 col-lg-4 col-6">
                <div data-container="metric_refunds"></div>
            </div>
            <div class="col-xl-2 col-lg-4 col-6">
                <div data-container="metric_failed"></div>
            </div>
            <div class="col-xl-2 col-lg-4 col-6">
                <div data-container="metric_unlocks"></div>
            </div>
            <div class="col-xl-2 col-lg-4 col-6">
                <div data-container="metric_avg_transaction"></div>
            </div>
        </div>
        `;
    }

    async onInit() {
        const defaults = {
            textColor: "#FFFFFF",
            granularity: 'days',
            trendRange: 4,
            trendOffset: 1,
            account: 'group-',
            chartType: 'bar',
            showTooltip: true,
            showXAxis: false,
            height: 35,
            chartWidth: '100%',
            color: 'rgba(245, 245, 255, 0.8)',
            fill: true,
            fillColor: 'rgba(245, 245, 255, 0.5)',
            smoothing: 0.2,
            showTrending: true,
        };

        this.transactionsChart = new MetricsMiniChartWidget({
            ...defaults,
            icon: "bi bi-receipt",
            title: 'Transactions',
            subtitle: '{{total}} <span class="subtitle-label">{{total_label}}</span>',
            background: "#0d6efd",
            slugs: ['vending_transaction_captured'],
            containerId: 'metric_transactions'
        });
        this.addChild(this.transactionsChart);

        this.revenueChart = new MetricsMiniChartWidget({
            ...defaults,
            icon: "bi bi-currency-dollar",
            title: 'Revenue',
            subtitle: '{{total}} <span class="subtitle-label">{{total_label}}</span>',
            background: "#198754",
            slugs: ['vending_revenue'],
            containerId: 'metric_revenue'
        });
        this.addChild(this.revenueChart);

        this.refundsChart = new MetricsMiniChartWidget({
            ...defaults,
            icon: "bi bi-arrow-counterclockwise",
            title: 'Refunds',
            subtitle: '{{total}} <span class="subtitle-label">{{total_label}}</span>',
            background: "#dc3545",
            slugs: ['vending_transaction_refunded'],
            containerId: 'metric_refunds'
        });
        this.addChild(this.refundsChart);

        this.failedChart = new MetricsMiniChartWidget({
            ...defaults,
            icon: "bi bi-exclamation-triangle-fill",
            title: 'Failed',
            subtitle: '{{total}} <span class="subtitle-label">{{total_label}}</span>',
            background: "#ffc107",
            textColor: "#212529",
            slugs: ['vending_transaction_failed'],
            containerId: 'metric_failed'
        });
        this.addChild(this.failedChart);

        this.unlocksChart = new MetricsMiniChartWidget({
            ...defaults,
            icon: "bi bi-unlock-fill",
            title: 'Unlocks Sent',
            subtitle: '{{total}} <span class="subtitle-label">{{total_label}}</span>',
            background: "#6f42c1",
            slugs: ['vending_unlock_sent'],
            containerId: 'metric_unlocks'
        });
        this.addChild(this.unlocksChart);

        this.avgTransactionChart = new MetricsMiniChartWidget({
            ...defaults,
            icon: "bi bi-graph-up",
            title: 'Avg Transaction',
            subtitle: '{{total}} <span class="subtitle-label">{{total_label}}</span>',
            background: "#fd7e14",
            slugs: ['vending_avg_transaction'],
            containerId: 'metric_avg_transaction'
        });
        this.addChild(this.avgTransactionChart);
    }

    async onAfterMount() {
        this.refreshValues();
    }

    async refreshValues() {
        const group = this.getApp().getActiveGroup();
        const account = `group-${group.id}`;
        const allWidgets = [
            this.transactionsChart, this.revenueChart,
            this.refundsChart, this.failedChart,
            this.unlocksChart, this.avgTransactionChart,
        ];
        allWidgets.forEach(c => {
            if (!c) return;
            c.account = account;
            if (c.chartOptions) c.chartOptions.account = account;
        });
        await Promise.allSettled(allWidgets.map(c => c?.refresh()));
    }
}


export default class VendingDashboardPage extends Page {
    constructor(options = {}) {
        super({
            ...options,
            title: 'Vending Dashboard',
            icon: "bi-speedometer2",
            className: 'dashboard-page'
        });
        this.pageSubtitle = 'Transaction and revenue overview';
    }

    async getTemplate() {
        return `
        <div class="dashboard-container container-lg">
            <div class="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <p class="text-muted mb-0">{{pageSubtitle}}</p>
                </div>
                <button type="button" class="btn btn-outline-secondary btn-sm" data-action="refresh-all">
                    <i class="bi bi-arrow-clockwise me-1"></i>Refresh
                </button>
            </div>

            <div data-container="metrics"></div>

            <div style="background:#fff;border-radius:12px;box-shadow:0 1px 3px rgba(0,0,0,0.08);overflow:hidden;">
                <div style="padding:1rem 1.25rem;border-bottom:1px solid #f0f0f0;">
                    <h6 style="margin:0;font-weight:700;font-size:0.95rem;">
                        <i class="bi bi-credit-card me-2"></i>Recent Transactions
                    </h6>
                </div>
                <div data-container="recent-transactions"></div>
            </div>
        </div>
        `;
    }

    async onInit() {
        this.metricsView = new MetricsView({ containerId: 'metrics' });
        this.addChild(this.metricsView);

        this.recentTransactions = new TableView({
            containerId: "recent-transactions",
            Collection: VendingTransactionList,
            requiresActiveGroup: true,
            hideActivePillNames: ["group"],
            searchable: false,
            filterable: false,
            clickAction: "view",
            columns: [
                { label: "Date", key: "created", formatter: "relative" },
                { label: "Terminal", key: "machine.name", formatter: "default('—')" },
                { label: "Amount", key: "amount", formatter: (cents, row) => {
                    if (!cents && cents !== 0) return '—';
                    const currency = row?.currency || 'USD';
                    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
                }},
                { label: "Status", key: "status|badge" },
            ],
        });
        this.addChild(this.recentTransactions);
    }

    async onEnter() {
        await super.onEnter();
        this.activeGroup = this.getApp().getActiveGroup();
        this.recentTransactions.collectionParams = {
            size: 10,
            sort: '-created',
            group: this.activeGroup.id,
        };
    }

    async onActionRefreshAll() {
        await this.refreshDashboard();
        this.getApp().toast.success('Dashboard refreshed');
    }

    async refreshDashboard() {
        this.activeGroup = this.getApp().getActiveGroup();
        this.recentTransactions.collectionParams = {
            size: 10,
            sort: '-created',
            group: this.activeGroup.id,
        };
        await Promise.allSettled([
            this.metricsView?.refreshValues(),
            this.recentTransactions?.collection?.fetch()
        ].filter(Boolean));
    }

    async onGroupChange(group) {
        this.refreshDashboard();
    }
}
